import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import toast from 'react-hot-toast';
import { AdminHero, SectionHead } from '@/components/admin/ui';
import { AuthedImage } from '@/components/media/AuthedImage';

interface PendingListing {
  id: string;
  title: string;
  brand: string;
  code?: string;
  category?: string;
  priceGBP: number;
  serialOrImei?: string;
  serialPhotoUrl?: string;
  imeiCheckResult?: 'CLEAN' | 'STOLEN' | 'BLACKLISTED' | 'UNKNOWN';
  authRequired?: boolean;
  authMissing?: string | null;
  sellerName: string;
  submittedAt: string;
  flags: string[];
}

interface PendingId {
  id: string;
  name: string;
  email: string;
  handle?: string | null;
  role: string;
  trustScore: number;
  submittedAt: string;
}

/**
 * The ID document endpoint is authenticated and audited (every view is
 * logged), so it can't be a plain <img src> - a bare <img> tag can't carry
 * the Authorization header. Fetches through the shared `api` client instead
 * and renders the result as a blob object URL.
 */
function IdDocumentImage({ userId }: { userId: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    api.get(`/admin/id-verifications/${userId}/document`, { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setUrl(objectUrl);
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [userId]);

  if (failed) return <span className="text-mute text-xs">Couldn’t load document</span>;
  if (!url) return <div className="w-full h-20 rounded-luxe bg-cream-deep animate-pulse" />;

  return (
    <a href={url} target="_blank" rel="noreferrer" className="block">
      <img src={url} alt="ID document" className="w-full h-20 object-cover rounded-luxe border border-warmline hover:shadow-glow transition" />
    </a>
  );
}

export default function VerificationQueue() {
  const qc = useQueryClient();
  const { data: rows = [] } = useQuery<PendingListing[]>({
    queryKey: ['admin', 'verify'],
    queryFn: async () => (await api.get('/admin/verifications')).data,
  });
  const { data: idRows = [] } = useQuery<PendingId[]>({
    queryKey: ['admin', 'id-verify'],
    queryFn: async () => (await api.get('/admin/id-verifications')).data,
  });

  const decide = useMutation({
    mutationFn: async ({ id, decision, note }: { id: string; decision: 'APPROVE' | 'REJECT'; note?: string }) =>
      (await api.post(`/admin/verifications/${id}`, { decision, note })).data,
    onSuccess: () => {
      toast.success('Decision recorded.');
      qc.invalidateQueries({ queryKey: ['admin', 'verify'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not record decision.'),
  });

  const decideId = useMutation({
    mutationFn: async ({ id, decision }: { id: string; decision: 'APPROVE' | 'REJECT' }) =>
      (await api.post(`/admin/id-verifications/${id}`, { decision })).data,
    onSuccess: () => {
      toast.success('Identity decision recorded.');
      qc.invalidateQueries({ queryKey: ['admin', 'id-verify'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not record decision.'),
  });

  return (
    <div className="p-6 md:p-10 space-y-6">
      <AdminHero
        eyebrow="Admin · Gatekeeping"
        title="Verification queue"
        subtitle="Listings screened for duplicate and known-stolen serials - reject anything STOLEN or BLACKLISTED. Identity documents grant the verified badge."
        stats={[
          { label: 'Listings', value: rows.length, accent: rows.length ? 'gold' : undefined },
          { label: 'ID checks', value: idRows.length, accent: idRows.length ? 'gold' : undefined },
        ]}
      />

      <SectionHead title="Listings awaiting review" count={rows.length} />
      <div className="card overflow-x-auto">
        {rows.length ? (
          <div className="min-w-[760px] divide-y divide-warmline">
            {rows.map((r) => (
              <div key={r.id} className="grid grid-cols-12 gap-3 items-center p-5 text-sm transition-colors hover:bg-cream-deep/50">
                <div className="col-span-4">
                  <div className="display text-lg">{r.title} {r.code && <span className="mono text-mute text-[10px] ml-1">{r.code}</span>}</div>
                  <div className="text-xs text-mute">{r.brand} · seller {r.sellerName}</div>
                  {r.serialOrImei && <div className="text-xs text-mute mt-1">IMEI: <code className="mono">{r.serialOrImei}</code></div>}
                  {r.serialPhotoUrl && (
                    <div className="text-xs mt-1 flex items-center gap-2">
                      <span className="pill pill-gold text-[10px]">🔍 Serial photo</span>
                      <AuthedImage path={r.serialPhotoUrl} className="w-16 h-16 object-cover rounded border border-warmline" />
                    </div>
                  )}
                  {r.authMissing && (
                    <div className="text-[11px] text-bad mt-1.5 flex items-start gap-1.5">
                      <span className="pill pill-bad text-[10px] shrink-0">⛔ Authenticity</span>
                      <span className="mt-0.5">{r.authMissing}</span>
                    </div>
                  )}
                </div>
                <div className="col-span-2 display text-xl">£{r.priceGBP.toFixed(0)}</div>
                <div className="col-span-2">
                  {r.imeiCheckResult && (
                    <span className={`pill text-[10px] ${r.imeiCheckResult === 'CLEAN' ? 'pill-ok' : r.imeiCheckResult === 'STOLEN' ? 'pill-bad' : 'pill-warn'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${r.imeiCheckResult === 'CLEAN' ? 'bg-ok' : r.imeiCheckResult === 'STOLEN' ? 'bg-bad' : 'bg-warn'}`} />
                      IMEI {r.imeiCheckResult}
                    </span>
                  )}
                </div>
                <div className="col-span-2">
                  {r.flags.map((f) => <span key={f} className="pill pill-warn mr-1 text-[10px]">{f}</span>)}
                </div>
                <div className="col-span-2 flex justify-end gap-2">
                  <button
                    onClick={() => decide.mutate({ id: r.id, decision: 'APPROVE' })}
                    disabled={Boolean(r.authMissing) || decide.isPending}
                    title={r.authMissing ?? undefined}
                    className="btn btn-gold btn-sm disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Approve
                  </button>
                  <button onClick={() => decide.mutate({ id: r.id, decision: 'REJECT' })} disabled={decide.isPending} className="btn btn-outline btn-sm">Reject</button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-10 text-center text-mute"><div className="text-2xl mb-1 opacity-60">✅</div>Listing queue is empty.</div>
        )}
      </div>

      {/* ── Identity (KYC) verification ── */}
      <SectionHead
        title="Identity checks"
        count={idRows.length}
        right={idRows.length > 0 ? <span className="pill pill-warn text-[11px]">{idRows.length} awaiting</span> : undefined}
      />
      <div className="card overflow-x-auto">
        {idRows.length ? (
          <div className="min-w-[680px] divide-y divide-warmline">
            {idRows.map((u) => (
              <div key={u.id} className="grid grid-cols-12 gap-3 items-center p-5 text-sm transition-colors hover:bg-cream-deep/50">
                <div className="col-span-2">
                  <IdDocumentImage userId={u.id} />
                </div>
                <div className="col-span-7">
                  <div className="font-medium text-ink">{u.name} {u.handle && <span className="text-mute text-xs">@{u.handle}</span>}</div>
                  <div className="text-xs text-mute">{u.email}</div>
                  <div className="text-[11px] text-mute mt-0.5">{u.role} · trust {u.trustScore} · submitted {new Date(u.submittedAt).toLocaleDateString()}</div>
                </div>
                <div className="col-span-3 flex justify-end gap-2">
                  <button onClick={() => decideId.mutate({ id: u.id, decision: 'APPROVE' })} disabled={decideId.isPending} className="btn btn-gold btn-sm">Verify</button>
                  <button onClick={() => decideId.mutate({ id: u.id, decision: 'REJECT' })} disabled={decideId.isPending} className="btn btn-outline btn-sm">Reject</button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-10 text-center text-mute"><div className="text-2xl mb-1 opacity-60">🪪</div>No identity documents awaiting review.</div>
        )}
      </div>
    </div>
  );
}
